import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  CircularProgress,
  Grid,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { weekDays } from "../constants/WeekDays";
import { getAllSlots, addOrUpdateSlot } from "../apis/Slot";

function SlotForm() {
  const navigate = useNavigate();
  const { day } = useParams();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    day: day || "",
    timeSlots: [],
  });

  const timeSlots = [
    "9 AM",
    "10 AM",
    "11 AM",
    "12 PM",
    "1 PM",
    "2 PM",
    "3 PM",
    "4 PM",
    "5 PM",
    "6 PM",
  ];

  useEffect(() => {
    if (day) {
      fetchSlotData();
    }
  }, [day]);

  const fetchSlotData = async () => {
    try {
      setLoading(true);
      const slots = await getAllSlots();
      const slotData = slots.find((slot) => slot.day === parseInt(day));
      if (slotData) {
        setFormData({
          day: slotData.day.toString(),
          timeSlots: slotData.availableSlots,
        });
      }
    } catch (error) {
      toast.error("Failed to fetch slot data");
      console.error("Error fetching slot data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDayChange = (event) => {
    setFormData((prev) => ({
      ...prev,
      day: event.target.value,
    }));
  };

  const handleTimeSlotChange = (timeSlot) => {
    setFormData((prev) => {
      const timeSlots = prev.timeSlots.includes(timeSlot)
        ? prev.timeSlots.filter((slot) => slot !== timeSlot)
        : [...prev.timeSlots, timeSlot];
      return {
        ...prev,
        timeSlots,
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.day) {
      toast.error("Please select a day");
      return;
    }
    if (formData.timeSlots.length === 0) {
      toast.error("Please select at least one time slot");
      return;
    }

    try {
      setLoading(true);
      await addOrUpdateSlot(formData.day, {
        availableSlots: formData.timeSlots,
      });
      toast.success("Slot updated successfully");
      navigate("/slots");
    } catch (error) {
      toast.error("Failed to update slot");
      console.error("Error updating slot:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 3 }}>
          {day ? "Edit Slot" : "Add New Slot"}
        </Typography>
        <form onSubmit={handleSubmit}>
          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Day</InputLabel>
              <Select
                value={formData.day}
                label="Day"
                onChange={handleDayChange}
                disabled={!!day}
              >
                {Object.entries(weekDays).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Available Time Slots
            </Typography>
            <Grid
              container
              spacing={2}
              sx={{
                "& .MuiGrid-item": {
                  width: "20%",
                  flexBasis: "20%",
                  maxWidth: "20%",
                },
              }}
            >
              {timeSlots.map((timeSlot) => (
                <Grid item key={timeSlot}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.timeSlots.includes(timeSlot)}
                        onChange={() => handleTimeSlotChange(timeSlot)}
                      />
                    }
                    label={timeSlot}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => navigate("/slots")}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
            >
              {day ? "Update" : "Create"}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
}

export default SlotForm;
